/**
 * Emails-tab dedupe helper.
 *
 * The Lead drawer's Emails tab merges two sources:
 *
 *   1. `GET /leads/{id}/emails` — every `emails` row where
 *      `emails.lead_id = leadId`. Captures direct sends that never
 *      spawned an `email_thread` (the row-action "Send email").
 *
 *   2. `thread.email_threads[i].emails[j]` — emails grouped by
 *      linked email_thread. Same email may appear here if it was
 *      part of a conversation that got linked to the lead.
 *
 * An email is in BOTH lists whenever it has a `lead_id` set AND
 * participates in a linked email_thread. Rendering both would show
 * the same message twice.
 *
 * The unique identity of a message is `email.id`. Everything else —
 * subject, recipient, timestamp, `thread_id` — is either non-unique
 * (multiple emails share a thread) or coincidentally-duplicatable
 * (rep sends "Follow up" twice, ten seconds apart, to the same
 * address). Do not dedupe on those.
 */

import type { LeadEmailRow, EmailThreadLink } from "./leadApi";

/**
 * Filter `leadEmails` to remove entries whose `id` already appears
 * anywhere in the grouped-thread payload. The passed `leadEmails` is
 * not mutated.
 *
 * When a thread has NO `emails` sub-array (e.g. the API stripped it,
 * or the payload is a compact list-view shape) the thread contributes
 * zero ids to the exclude-set — the lead emails pass through
 * unchanged. This is the safe default: showing the direct row once is
 * better than hiding it because we couldn't confirm the thread's
 * membership.
 */
export function dedupeLeadEmails(
  leadEmails: LeadEmailRow[],
  threads: EmailThreadLink[] | undefined,
): LeadEmailRow[] {
  if (!threads || threads.length === 0) return leadEmails;

  const threadedIds = new Set<number>();
  for (const t of threads) {
    if (!t.emails) continue;
    for (const e of t.emails) {
      if (typeof e?.id === "number") threadedIds.add(e.id);
    }
  }

  if (threadedIds.size === 0) return leadEmails;

  return leadEmails.filter((e) => !threadedIds.has(e.id));
}
